import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  usePatchFloorSettingsMutation,
  usePatchRepSettingsMutation,
  useSalesSettingsQuery,
} from '@/client/api/sales/sales';
import type {
  SalesConversationChannel,
  SalesFloorSettings,
  SalesRepSettings,
  SalesSettingsOverview,
  SalesSubjectSettings,
} from '@/client/types/sales';
import { SalesPageShell } from './components/SalesPageShell';
import { EmptyState, Panel, btnPrimary, selectCls, toastSales } from './components/sales-ui';

const CHANNELS: { id: SalesConversationChannel; label: string }[] = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'sms', label: 'SMS' },
  { id: 'email', label: 'Email' },
];
const inputCls = 'rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-800';

/** Floor-wide: which channels really send, cold WhatsApp, senders, quiet hours. */
function FloorPanel({ data }: { data: SalesSettingsOverview }) {
  const save = usePatchFloorSettingsMutation();
  const [draft, setDraft] = useState<SalesFloorSettings>({});
  useEffect(() => {
    setDraft({
      dispatch: data.floor.dispatch,
      whatsappCold: data.floor.whatsappCold,
      salesMailbox: data.floor.salesMailbox,
      whatsappFrom: data.floor.whatsappFrom,
      quietHours: data.floor.quietHours ?? null,
    });
  }, [data]);
  const fromKeystore = (key: string) =>
    data.floor.fromKeystore.includes(key) ? <span className="ml-2 text-xs text-gray-400">(currently from the keystore)</span> : null;

  return (
    <Panel
      title="Floor"
      actions={
        <button
          type="button"
          className={btnPrimary}
          disabled={save.isPending}
          onClick={() => save.mutate(draft, { onSuccess: () => toast.success('Floor settings saved'), onError: toastSales })}
        >
          Save floor settings
        </button>
      }
    >
      <div className="grid gap-6 md:grid-cols-2">
        <fieldset>
          <legend className="text-sm font-medium text-gray-900">
            Sending really sends{fromKeystore('dispatch')}
          </legend>
          <p className="mb-2 text-xs text-gray-500">Off: a send is recorded but nothing goes out.</p>
          {CHANNELS.map((c) => (
            <label key={c.id} className="flex items-center gap-2 py-1 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={Boolean(draft.dispatch?.[c.id])}
                onChange={(e) => setDraft({ ...draft, dispatch: { ...draft.dispatch, [c.id]: e.target.checked } })}
              />
              {c.label}
            </label>
          ))}
        </fieldset>

        <fieldset>
          <legend className="text-sm font-medium text-gray-900">Cold WhatsApp{fromKeystore('whatsappCold')}</legend>
          <label className="mt-2 flex items-start gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              className="mt-1"
              checked={Boolean(draft.whatsappCold)}
              onChange={(e) => setDraft({ ...draft, whatsappCold: e.target.checked })}
            />
            <span>
              Allow WhatsApp to prospects without recorded consent.
              <span className="block text-xs text-gray-500">
                Only reps given cold outreach below may use it. Do-not-contact, caps, spacing and opt-outs still apply.
              </span>
            </span>
          </label>
        </fieldset>

        <label className="block text-sm">
          <span className="font-medium text-gray-900">Sales email goes from{fromKeystore('salesMailbox')}</span>
          <select
            className={`${selectCls} mt-1 block w-full`}
            value={draft.salesMailbox || ''}
            onChange={(e) => setDraft({ ...draft, salesMailbox: e.target.value })}
          >
            {data.options.mailboxes.map((m) => (
              <option key={m.email} value={m.email}>
                {m.name} &lt;{m.email}&gt;
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="font-medium text-gray-900">Sales WhatsApp goes from</span>
          <select
            className={`${selectCls} mt-1 block w-full`}
            value={draft.whatsappFrom || ''}
            onChange={(e) => setDraft({ ...draft, whatsappFrom: e.target.value })}
          >
            {data.options.whatsappSenders.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="md:col-span-2">
          <legend className="text-sm font-medium text-gray-900">Sending hours (Accra time)</legend>
          <label className="mt-2 flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={Boolean(draft.quietHours)}
              onChange={(e) =>
                setDraft({ ...draft, quietHours: e.target.checked ? { start: '08:00', end: '20:00' } : null })
              }
            />
            Only send between set hours; a send outside them is refused with when it can go.
          </label>
          {draft.quietHours && (
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="time"
                aria-label="Sending starts"
                className={inputCls}
                value={draft.quietHours.start}
                onChange={(e) => setDraft({ ...draft, quietHours: { ...draft.quietHours!, start: e.target.value } })}
              />
              to
              <input
                type="time"
                aria-label="Sending ends"
                className={inputCls}
                value={draft.quietHours.end}
                onChange={(e) => setDraft({ ...draft, quietHours: { ...draft.quietHours!, end: e.target.value } })}
              />
            </div>
          )}
        </fieldset>
      </div>
    </Panel>
  );
}

/** One rep's overrides. Blank means "the floor's". */
function RepRow({
  rep,
  floorCap,
}: {
  rep: SalesSettingsOverview['reps'][number];
  floorCap: number;
}) {
  const save = usePatchRepSettingsMutation();
  const [draft, setDraft] = useState<SalesRepSettings>(rep.settings);
  useEffect(() => setDraft(rep.settings), [rep.settings]);
  const channels = draft.channels ?? CHANNELS.map((c) => c.id);
  const toggleChannel = (id: SalesConversationChannel, on: boolean) => {
    const next = on ? [...channels, id] : channels.filter((c) => c !== id);
    setDraft({ ...draft, channels: next });
  };

  const submit = () => {
    const allChannels = channels.length === CHANNELS.length;
    const limits = Object.fromEntries(
      Object.entries(draft.dailyLimits || {}).filter(([, v]) => v !== undefined && v !== null && !Number.isNaN(v)),
    );
    save.mutate(
      {
        repId: rep.id,
        patch: {
          claimCap: draft.claimCap ?? null,
          channels: allChannels ? null : channels,
          dailyLimits: Object.keys(limits).length ? limits : null,
          coldOutreach: draft.coldOutreach ? true : null,
        },
      },
      { onSuccess: () => toast.success(`Saved ${rep.name}`), onError: toastSales },
    );
  };

  return (
    <tr className="border-t border-gray-100 align-top">
      <td className="py-3 pr-4">
        <p className="font-medium text-gray-900">{rep.name}</p>
        <p className="text-xs text-gray-500">
          {rep.role} · {rep.desk}
        </p>
      </td>
      <td className="py-3 pr-4">
        <input
          type="number"
          min={0}
          aria-label={`Claim cap for ${rep.name}`}
          placeholder={String(floorCap)}
          className={`${inputCls} w-20`}
          value={draft.claimCap ?? ''}
          onChange={(e) => setDraft({ ...draft, claimCap: e.target.value === '' ? undefined : Number(e.target.value) })}
        />
      </td>
      <td className="py-3 pr-4">
        {CHANNELS.map((c) => (
          <label key={c.id} className="flex items-center gap-2 text-sm text-gray-800">
            <input type="checkbox" checked={channels.includes(c.id)} onChange={(e) => toggleChannel(c.id, e.target.checked)} />
            {c.label}
          </label>
        ))}
      </td>
      <td className="py-3 pr-4">
        {CHANNELS.map((c) => (
          <label key={c.id} className="mb-1 flex items-center gap-2 text-xs text-gray-600">
            <input
              type="number"
              min={0}
              aria-label={`${c.label} per day for ${rep.name}`}
              placeholder="No limit"
              className={`${inputCls} w-24`}
              value={draft.dailyLimits?.[c.id] ?? ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  dailyLimits: { ...draft.dailyLimits, [c.id]: e.target.value === '' ? undefined : Number(e.target.value) },
                })
              }
            />
            {c.label}
          </label>
        ))}
      </td>
      <td className="py-3 pr-4">
        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={Boolean(draft.coldOutreach)}
            onChange={(e) => setDraft({ ...draft, coldOutreach: e.target.checked })}
          />
          Allowed
        </label>
      </td>
      <td className="py-3">
        <button type="button" className={btnPrimary} disabled={save.isPending} onClick={submit}>
          Save
        </button>
      </td>
    </tr>
  );
}

function describe(settings: SalesSubjectSettings): string[] {
  const out: string[] = [];
  if (settings.pinnedRepId) out.push('Pinned to a rep');
  if (settings.channelCaps) out.push(`Own caps: ${Object.keys(settings.channelCaps).join(', ')}`);
  if (settings.outreachMinHours) out.push(`Own spacing: ${Object.keys(settings.outreachMinHours).join(', ')}`);
  if (settings.preferredChannel) out.push(`Prefers ${settings.preferredChannel}`);
  if (settings.contactHours) out.push(`Contact ${settings.contactHours.start}-${settings.contactHours.end}`);
  if (settings.consentSource) out.push(`Consent source: ${Object.keys(settings.consentSource).join(', ')}`);
  return out;
}

/**
 * Sales settings — for the Sales administrator and super admins
 * (`sales.settings.manage`). The floor's day-to-day rules (claim windows,
 * caps, SLAs) stay on Rules; this page is how Sales is set up.
 */
export default function SalesSettingsPage() {
  const settings = useSalesSettingsQuery();
  const data = settings.data;

  return (
    <SalesPageShell
      title="Settings"
      description="How Sales sends, and the overrides for each person on the floor and each prospect. Claim windows, caps and SLAs stay on Rules."
      crumbs={[{ label: 'Sales', href: '/admin/sales' }, { label: 'Settings' }]}
      icon={Settings2}
    >
      {settings.isLoading && <EmptyState label="Loading settings..." />}
      {settings.isError && <EmptyState label="Could not load Sales settings. You need the Sales settings permission." />}
      {data && (
        <div className="space-y-4">
          <FloorPanel data={data} />

          <Panel title="People on the floor">
            <p className="mb-3 text-xs text-gray-500">Blank means the floor's setting. All channels ticked means no restriction.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="pb-2 pr-4 font-medium">Person</th>
                    <th className="pb-2 pr-4 font-medium">Claim cap</th>
                    <th className="pb-2 pr-4 font-medium">May send on</th>
                    <th className="pb-2 pr-4 font-medium">Per day</th>
                    <th className="pb-2 pr-4 font-medium">Cold outreach</th>
                    <th className="pb-2" />
                  </tr>
                </thead>
                <tbody>
                  {data.reps.map((rep) => (
                    <RepRow key={rep.id} rep={rep} floorCap={data.floorClaimCap} />
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="Prospects with their own settings">
            {data.subjects.length === 0 ? (
              <p className="text-sm text-gray-500">None yet. Set them from a prospect's page, under Contact settings.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {data.subjects.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <Link to={`/admin/sales/prospects/${s.id}`} className="font-medium text-gray-900 hover:underline">
                      {s.displayName}
                    </Link>
                    <span className="text-xs text-gray-500">{describe(s.settings).join(' · ')}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}
    </SalesPageShell>
  );
}
